import {
  AddPassportEvents,
  CommonLandingEvents,
  IAnalytics,
  LandingEvents,
  ListingEvents,
  OrderDetailsEvents,
  OrderSummaryEvents,
  PageLoadErrorEvent,
  PageLoadEvent,
  ThankYouEvents,
  TravDetailsEvents,
} from "./IAnalytics";

import { isPartnerGI } from "@tm/insurance/utils";
import { pushEvent } from "../..";
import { pageNameCategoryMap } from "./constants";

type EventType = "pageView" | "tm_ins_listing_view" | "ctaClick";

interface IPageInfo {
  pageName: string;
  lob?: string;
  section?: string;
  abVariant?: string;
}

interface GIOmniDataClickEventType {
  event: EventType;
  component: {
    name: string;
    action: string;
    type?: string;
    position?: string;
  };
  page?: {
    pageInfo: IPageInfo;
  };
}

interface GIOmniDataPageViewType {
  event: EventType;
  page: {
    pageInfo: IPageInfo;
  };
  booking?: {
    pax?: string;
    routeCity?: string;
    departureDate?: string;
    returnDate?: string;
    purposeOfTravel?: string;
    sumInsuredValue?: string;
    bookingId?: string;
    paymentStatus?: string;
  };
  cta?: {
    name: string;
  };
  error?: string;
}

// Add a global queue for GI analytics events
declare global {
  interface Window {
    giAnalyticsQueue?: any[];
  }
}

function flushGIAnalyticsQueue() {
  if (
    typeof window !== "undefined" &&
    window.giAnalyticsQueue &&
    typeof pushEvent === "function"
  ) {
    window.giAnalyticsQueue.forEach((event) => {
      pushEvent(event);
    });
    window.giAnalyticsQueue = [];
  }
}

if (isPartnerGI() && typeof window !== "undefined") {
  window.addEventListener("gi-analytics:ready", flushGIAnalyticsQueue);
}

function safePushEvent(event: any) {
  if (typeof window === "undefined" || typeof pushEvent !== "function") {
    if (typeof window !== "undefined") {
      window.giAnalyticsQueue = window.giAnalyticsQueue || [];
      window.giAnalyticsQueue.push(event);
    }
    // If window is undefined (SSR), do nothing
    return;
  }
  pushEvent(event);
}

export class GIAnalytics implements IAnalytics {
  trackPageLoadEvent(event: PageLoadEvent, type?: string) {
    const { pageName, lob, siteSection } = pageNameCategoryMap[event?.pageName];

    let omniData: GIOmniDataPageViewType = {
      event: "pageView",
      page: {
        pageInfo: {
          pageName,
          lob,
          section: siteSection,
          ...(event.experiment && {
            abVariant: event.experiment,
          }),
        },
      },
      booking: {
        ...(event.bookingId && {
          bookingId: event.bookingId,
        }),
        ...(event.bookingStatus && {
          paymentStatus: event.bookingStatus,
        }),
      },
    };

    if (event.pageName === "LISTING") {
      omniData = {
        ...omniData,
        event: "tm_ins_listing_view",
        booking: {
          ...(event.country && {
            routeCity: event.country,
          }),
          ...(event.startDate && {
            departureDate: event.startDate,
          }),
          ...(event.endDate && {
            returnDate: event.endDate,
          }),
          ...(event.purpose && {
            purposeOfTravel: event.purpose,
          }),
          ...(event.travellers && {
            pax: event.travellers,
          }),
        },
      };
    }

    safePushEvent(omniData);
  }

  trackRawPageLoadEvent(event: any, type?: string) {
    const omniData: GIOmniDataPageViewType = {
      event: "pageView",
      page: {
        pageInfo: {
          pageName: event?.eVar15,
        },
      },
    };
    safePushEvent(omniData);
  }

  trackPageLoadErrorEvent(event: PageLoadErrorEvent, type?: string) {
    const { pageName } = pageNameCategoryMap[event?.pageName];

    const omniData: GIOmniDataPageViewType = {
      event: "pageView",
      page: {
        pageInfo: {
          pageName,
        },
      },
      cta: {
        name: event.actionType,
      },
      error: event.error,
    };

    safePushEvent(omniData);
  }

  trackCommonLandingEvents(event: CommonLandingEvents, eventType: string = "pageView") {
    //Track when this comes in GI
  }

  trackLandingEvents(event: LandingEvents, eventType: string = "pageView", type?: string) {
    const { pageName, lob } = pageNameCategoryMap["LANDING"];
    let omniData: GIOmniDataPageViewType | GIOmniDataClickEventType;
    if (eventType === "pageView") {
      omniData = {
        event: "pageView",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    } else {
      omniData = {
        event: "ctaClick",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    }

    if (
      event.actionType === "other_countries_clicked" ||
      event.actionType === "popular_country_selected" ||
      event.actionType === "searched_countries" ||
      event.actionType === "selected_countries"
    ) {
      omniData = {
        ...omniData,
        booking: {
          routeCity: event.payload,
        },
      };
    } else if (event.actionType === "travel_date_selected") {
      omniData = {
        ...omniData,
        booking: {
          departureDate: event.payload.departure,
          returnDate: event.payload.arrival,
        },
      };
    } else if (event.actionType === "error_shown") {
      omniData = {
        ...omniData,
        error: event.payload,
      };
    }
    safePushEvent(omniData);
  }

  trackListingEvents(event: ListingEvents, eventType: string = "pageView", type?: string) {
    const { pageName, lob } = pageNameCategoryMap["LISTING"];
    let omniData: GIOmniDataPageViewType | GIOmniDataClickEventType;
    if (eventType === "pageView") {
      omniData = {
        event: "pageView",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    } else {
      omniData = {
        event: "ctaClick",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    }

    if (event.actionType === "sum_insured_selected") {
      omniData = {
        ...omniData,
        booking: {
          sumInsuredValue: event.payload,
        },
      };
    }
    safePushEvent(omniData);
  }

  trackTravDetailsEvents(
    event: TravDetailsEvents,
    eventType: string = "pageView",
    type?: string
  ) {
    const { pageName, lob } = pageNameCategoryMap["REVIEW"];
    let omniData: GIOmniDataPageViewType | GIOmniDataClickEventType;
    if (eventType === "pageView") {
      omniData = {
        event: "pageView",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    } else {
      omniData = {
        event: "ctaClick",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    }
    if (event.actionType === "error_shown") {
      omniData = {
        ...omniData,
        error: event.payload,
      };
    }
    safePushEvent(omniData);
  }

  trackThankYouEvents(event: ThankYouEvents, eventType: string = "pageView", type?: string) {
    const { pageName, lob } = pageNameCategoryMap[event.pageName];
    let omniData: GIOmniDataPageViewType | GIOmniDataClickEventType;
    if (eventType === "pageView") {
      omniData = {
        event: "pageView",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    } else {
      omniData = {
        event: "ctaClick",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    }
    safePushEvent(omniData);
  }

  trackAddPassportEvents(
    event: AddPassportEvents,
    eventType: string = "pageView"
  ) {
    const { pageName, lob } = pageNameCategoryMap["ADD_PASSPORT"];
    let omniData: GIOmniDataPageViewType | GIOmniDataClickEventType;
    if (eventType === "pageView") {
      omniData = {
        event: "pageView",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    } else {
      omniData = {
        event: "ctaClick",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    }
    safePushEvent(omniData);
  }

  trackOrderSummaryEvents(
    event: OrderSummaryEvents,
    eventType: string = "pageView"
  ) {
    const { pageName, lob } = pageNameCategoryMap["ORDER_SUMMARY"];
    let omniData: GIOmniDataPageViewType | GIOmniDataClickEventType;
    if (eventType === "pageView") {
      omniData = {
        event: "pageView",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    } else {
      omniData = {
        event: "ctaClick",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    }
    safePushEvent(omniData);
  }

  trackOrderDetailsEvents(
    event: OrderDetailsEvents,
    eventType: string = "pageView"
  ) {
    const { pageName, lob } = pageNameCategoryMap["ORDER_DETAILS"];
    let omniData: GIOmniDataPageViewType | GIOmniDataClickEventType;
    if (eventType === "pageView") {
      omniData = {
        event: "pageView",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    } else {
      omniData = {
        event: "ctaClick",
        page: {
          pageInfo: {
            pageName,
            lob,
          },
        },
        cta: {
          name: event.actionType,
        },
      };
    }
    safePushEvent(omniData);
  }
}
